import { ORDER_APIS } from "@/api_framework/api_config";
import {
  IOrderManagementFilterBody,
  IOrderManagementOrderListResponse,
  IOrderManagementOrderListData,
  IOrderManagementOrderListPaginated,
} from "@/api_framework/api_modals/Orders";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setOrderList } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { store } from "@/app_redux/stores/root_store";
import axios from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";

AxiosCurlirize(axios, () => {});

const isOrderSearchActive = () => {
  const state = store.getState();
  return Boolean(state?.routes?.auth?.ORDER_MANAGEMENT?.state?.isOrderSearchActive);
};

export const useOrderManagementOrderListAPI = () => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const controllerRef = useRef<AbortController | null>(null);

  const normalizeOrderResponse = (
    apiData: IOrderManagementOrderListData[] | IOrderManagementOrderListPaginated | null,
  ): { orders: IOrderManagementOrderListData[]; totalOrders: number } => {
    if (!apiData) {
      return { orders: [], totalOrders: 0 };
    }

    // Case 1: Non-paginated (array)
    if (Array.isArray(apiData)) {
      return {
        orders: apiData,
        totalOrders: apiData.length,
      };
    }

    // Case 2: Paginated (object)
    return {
      orders: apiData.data ?? [],
      totalOrders: apiData.total_orders ?? apiData.data?.length ?? 0,
    };
  };

  const execute = useCallback(
    async (filterBody: IOrderManagementFilterBody) => {
      dispatch(
        setLoading({
          key: "isQcOrderListLoadingInProgress",
          status: true,
        }),
      );

      try {
        // Abort previous request
        if (controllerRef.current) {
          controllerRef.current.abort();
        }

        controllerRef.current = new AbortController();

        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);

        const response = await axios.post<IOrderManagementOrderListResponse>(
          ORDER_APIS?.ORDER_LIST?.baseURL ?? "",
          filterBody,
          {
            headers: {
              Authorization: accessToken ?? "",
              "Content-Type": "application/json",
            },
            signal: controllerRef.current.signal,
          },
        );

        if (response.data.status) {
          if (isOrderSearchActive()) return;

          const { orders, totalOrders } = normalizeOrderResponse(response.data.data);

          dispatch(
            setOrderList({
              data: orders,
              totalOrders,
            }),
          );
        } else {
          if (isOrderSearchActive()) return;

          dispatch(
            setOrderList({
              data: [],
              totalOrders: 0,
            }),
          );
        }
      } catch (error: unknown) {
        // Request was aborted
        if (axios.isCancel(error) || (error instanceof DOMException && error.name === "AbortError")) {
          console.log("Request aborted");
          return;
        }

        if (axios.isAxiosError(error)) {
          sendCurl(error.config?.["curlCommand"]);
          toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
        } else {
          toast.error("Unexpected error occurred.");
        }

        if (isOrderSearchActive()) return;

        dispatch(
          setOrderList({
            data: [],
            totalOrders: 0,
          }),
        );
      } finally {
        dispatch(
          setLoading({
            key: "isQcOrderListLoadingInProgress",
            status: false,
          }),
        );
      }
    },
    [dispatch, configuration, sendCurl],
  );

  return execute;
};
